import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Mail } from 'lucide-react';

type Phase = 'loading' | 'output';

const LOADING_DURATION_MS = 6500;
const OUTPUT_DURATION_MS = 6500;
const FADE_MS = 500;

const GRID_SIZE = 7;
interface Dot { row: number; col: number; delay: number }
const DOTS: Dot[] = (() => {
  const arr: Dot[] = [];
  const center = (GRID_SIZE - 1) / 2;
  for (let r = 0; r < GRID_SIZE; r++) {
    for (let c = 0; c < GRID_SIZE; c++) {
      const dx = r - center;
      const dy = c - center;
      const d = Math.sqrt(dx * dx + dy * dy);
      arr.push({ row: r, col: c, delay: d * 0.18 });
    }
  }
  return arr;
})();

const stageFromProgress = (p: number): string => {
  if (p < 20) return 'Reading filings…';
  if (p < 50) return 'Extracting stakeholder positions…';
  if (p < 80) return 'Identifying themes…';
  return 'Drafting your summary…';
};

const TOPICS = [
  'Spectrum sharing',
  'Spectrum piggybacking',
  'WCS interference',
  'Coexistence',
  'Power limits',
];

/**
 * Cycling hero demo: shows the live loading screen, then reveals the
 * generated summary, then loops. Replaces the static mock card.
 */
export const HeroSummaryDemo: React.FC = () => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [progress, setProgress] = useState(0);

  // Phase cycle
  useEffect(() => {
    const dur = phase === 'loading' ? LOADING_DURATION_MS : OUTPUT_DURATION_MS;
    const id = setTimeout(() => {
      setPhase(p => (p === 'loading' ? 'output' : 'loading'));
      if (phase === 'output') setProgress(0); // reset for next loading round
    }, dur);
    return () => clearTimeout(id);
  }, [phase]);

  // Progress ramp during loading
  useEffect(() => {
    if (phase !== 'loading') return;
    const steps = 50;
    const stepMs = LOADING_DURATION_MS / steps;
    let p = 0;
    const id = setInterval(() => {
      p += 100 / steps;
      setProgress(Math.min(100, p));
      if (p >= 100) clearInterval(id);
    }, stepMs);
    return () => clearInterval(id);
  }, [phase]);

  const stage = useMemo(() => stageFromProgress(progress), [progress]);

  return (
    <div className="relative mx-auto w-full max-w-lg lg:max-w-none">
      <style>{`
        @keyframes cg-hero-dot {
          0%, 100% { transform: scale(0.32); opacity: 0.18; }
          50% { transform: scale(1); opacity: 1; }
        }
        @keyframes cg-hero-halo {
          0%, 100% { transform: scale(0.88); opacity: 0.25; }
          50% { transform: scale(1.08); opacity: 0.55; }
        }
        @keyframes cg-hero-shimmer {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(120%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .cg-hero-dot, .cg-hero-halo, .cg-hero-shimmer { animation: none !important; }
        }
      `}</style>

      {/* Floating decorative pills (kept from prior hero) */}
      <div className="absolute -left-4 -top-6 z-10 hidden rotate-[-3deg] rounded-full border border-cg-border bg-white px-3 py-1 text-xs font-medium text-neutral-700 shadow-sm sm:block">
        <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-cg-green" />
        Spectrum Piggybacking
      </div>
      <div className="absolute -right-3 -top-3 z-10 hidden rotate-[2deg] rounded-full border border-cg-border bg-white px-3 py-1 text-xs font-medium text-neutral-700 shadow-sm sm:block">
        <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-blue-500" />
        Definition of TT&amp;C
      </div>

      {/* Card with crossfade between phases. Both panels live in the same
          CSS grid cell so the card sizes to the taller panel and doesn't
          jiggle when phases swap. */}
      <div className="relative grid rounded-2xl border border-cg-border bg-white shadow-xl shadow-cg-nav/[0.08]">
        {/* Loading panel */}
        <div
          aria-hidden={phase !== 'loading'}
          className="transition-opacity"
          style={{
            gridArea: '1 / 1',
            opacity: phase === 'loading' ? 1 : 0,
            transitionDuration: `${FADE_MS}ms`,
            pointerEvents: phase === 'loading' ? 'auto' : 'none',
          }}
        >
          <div className="p-6 sm:p-8">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-cg-green">
                  Generating summary
                </p>
                <p className="font-display text-lg font-medium text-cg-ink">
                  Docket 26-54 — Space Modernization
                </p>
              </div>
              <span className="inline-flex items-center rounded-full border border-cg-green/30 bg-cg-green/10 px-2.5 py-0.5 text-[11px] font-semibold text-cg-green">
                41 filings
              </span>
            </div>

            {/* Animated dot grid */}
            <div className="relative mx-auto mb-6 flex h-44 w-44 items-center justify-center">
              <div
                className="cg-hero-halo absolute inset-0 rounded-full bg-cg-green/35 blur-3xl"
                style={{ animation: 'cg-hero-halo 3.6s ease-in-out infinite' }}
              />
              <div
                className="relative grid"
                style={{ gridTemplateColumns: `repeat(${GRID_SIZE}, minmax(0, 1fr))`, gap: '11px' }}
              >
                {DOTS.map(({ row, col, delay }) => (
                  <div
                    key={`${row}-${col}`}
                    className="cg-hero-dot h-2 w-2 rounded-full bg-cg-green"
                    style={{ animation: `cg-hero-dot 2.4s ease-in-out ${delay}s infinite` }}
                  />
                ))}
              </div>
            </div>

            <p className="text-center text-sm font-medium text-cg-green">{stage}</p>

            {/* Progress bar */}
            <div className="mt-4">
              <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-neutral-200/70">
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-cg-green transition-all duration-500 ease-out"
                  style={{ width: `${Math.max(4, progress)}%` }}
                />
                <div
                  className="cg-hero-shimmer absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/70 to-transparent"
                  style={{ animation: 'cg-hero-shimmer 2.2s linear infinite' }}
                />
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px] text-neutral-500">
                <span>~3 min total</span>
                <span>{Math.round(progress)}%</span>
              </div>
            </div>

            {/* Topic chips */}
            <div className="mt-5 flex flex-wrap justify-center gap-1.5">
              {TOPICS.map((t, i) => (
                <span
                  key={i}
                  className="rounded-full border border-cg-border bg-white px-2.5 py-1 text-xs text-cg-ink"
                >
                  {t}
                </span>
              ))}
            </div>

            <div className="mt-5 flex items-start gap-2 rounded-xl border border-cg-border/70 bg-cg-cream/40 px-3 py-2">
              <Mail className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-cg-green" />
              <p className="text-[11px] leading-relaxed text-neutral-600">
                We'll email you a link too — feel free to step away.
              </p>
            </div>
          </div>
        </div>

        {/* Output panel */}
        <div
          aria-hidden={phase !== 'output'}
          className="transition-opacity"
          style={{
            gridArea: '1 / 1',
            opacity: phase === 'output' ? 1 : 0,
            transitionDuration: `${FADE_MS}ms`,
            pointerEvents: phase === 'output' ? 'auto' : 'none',
          }}
        >
          <div className="p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-cg-green">
                  Comment Summary
                </p>
                <p className="font-display text-lg font-medium text-cg-ink">
                  Docket 26-54 — Space Modernization
                </p>
              </div>
              <span className="inline-flex items-center rounded-full border border-cg-green/30 bg-cg-green/10 px-2.5 py-0.5 text-[11px] font-semibold text-cg-green">
                41 filings
              </span>
            </div>

            <div className="mb-5 grid grid-cols-3 gap-3 rounded-xl border border-cg-border bg-cg-cream/60 p-3 text-center">
              <div>
                <div className="font-display text-xl font-medium text-cg-ink">38</div>
                <div className="text-[10px] uppercase tracking-wider text-neutral-500">Stakeholders</div>
              </div>
              <div className="border-x border-cg-border">
                <div className="font-display text-xl font-medium text-cg-ink">10</div>
                <div className="text-[10px] uppercase tracking-wider text-neutral-500">Topics</div>
              </div>
              <div>
                <div className="font-display text-xl font-medium text-cg-ink">191</div>
                <div className="text-[10px] uppercase tracking-wider text-neutral-500">Positions</div>
              </div>
            </div>

            <div className="rounded-xl border border-neutral-200 bg-white p-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-cg-ink">Capella Space Corp.</p>
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                  Intersatellite Links
                </span>
              </div>
              <p className="mb-3 text-sm leading-relaxed text-neutral-700">
                Recommends the FCC authorize by rule the use of MSS intersatellite links for telemetry, tracking, and command (TT&amp;C) and data downlinks.
              </p>
              <div className="border-l-2 border-cg-green/40 pl-3">
                <p className="text-xs italic leading-relaxed text-neutral-600">
                  "The Commission should authorize by rule licensed satellite operators to use MSS ISLs to provide TT&amp;C and data relay services."
                </p>
                <p className="mt-1.5 text-[11px] font-medium text-neutral-500">
                  Page 2 · Capella_Comments.pdf
                </p>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between text-xs text-neutral-500">
              <span>+ 189 more positions</span>
              <span className="inline-flex items-center gap-1">
                Share link
                <ArrowRight className="h-3 w-3" />
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Phase indicator dots underneath the card */}
      <div className="mt-4 flex justify-center gap-1.5">
        <span
          className={`h-1.5 rounded-full transition-all duration-300 ${
            phase === 'loading' ? 'w-6 bg-cg-green' : 'w-1.5 bg-neutral-300'
          }`}
        />
        <span
          className={`h-1.5 rounded-full transition-all duration-300 ${
            phase === 'output' ? 'w-6 bg-cg-green' : 'w-1.5 bg-neutral-300'
          }`}
        />
      </div>
    </div>
  );
};

export default HeroSummaryDemo;
